import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import {
  listRejectionReasons,
  type RejectionReasonOption,
} from '../../api/rejectionReasons.js';
import {
  getClosureMeta,
  type ClosureMeta,
} from '../../api/closureMeta.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

/** Render an enum/master code as a human-readable label. */
function humanize(code: string): string {
  return code
    .toLowerCase()
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/**
 * M11 L11.03 — Company Approval dialog.
 *
 * Shows pre-conditions (read-only, fed from the verification detail) and
 * exposes the four decision actions (approve / reject / rework / evidence)
 * against `/v1/closure/approval/:verificationId/...`.
 *
 * The Approve button is disabled until every pre-condition is satisfied.
 */
interface DetailResponse {
  readonly verification: {
    readonly verificationId: string;
    readonly status: string;
    readonly overallQualityScore: number | null;
    readonly brandComplianceScore: number | null;
    readonly photoComplianceScore: number | null;
    readonly gpsValidated: boolean | null;
    readonly areaVariancePct: number | null;
  };
  /** The job's number — how the job is known on screen. */
  readonly jobNumber: string | null;
  readonly checklist: ReadonlyArray<unknown>;
  readonly photos: ReadonlyArray<unknown>;
}

type Decision = 'APPROVE' | 'REJECT' | 'REWORK' | 'EVIDENCE';

export function CompanyApprovalPage(): ReactNode {
  const { t } = useTranslation();
  const { decimal } = useBusinessNumber();
  const { verificationId = '' } = useParams<{ verificationId: string }>();
  const { token } = useAuth();
  const [data, setData] = useState<DetailResponse | null>(null);
  const [meta, setMeta] = useState<ClosureMeta | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [reasons, setReasons] = useState<readonly RejectionReasonOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [remarks, setRemarks] = useState<string>('');
  const [reasonCode, setReasonCode] = useState<string>('');
  const [reworkTarget, setReworkTarget] = useState<string>('');
  const [evidenceKind, setEvidenceKind] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);

  useEffect(() => {
    let alive = true;
    if (!verificationId) return undefined;
    apiRequest<DetailResponse>(
      `/v1/closure/review/${encodeURIComponent(verificationId)}`,
      { token },
    )
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e) => {
        if (alive) setError((e as Error).message);
      });
    return () => {
      alive = false;
    };
  }, [verificationId, token]);

  useEffect(() => {
    let alive = true;
    getClosureMeta(token)
      .then((m) => {
        if (!alive) return;
        setMeta(m);
        setReworkTarget((prev) => prev || m.reworkTargets[0] || '');
        setEvidenceKind((prev) => prev || m.evidenceKinds[0] || '');
      })
      // Without the meta there are no tenant thresholds; the failure is said
      // (apart from `error`, which every decision clears) and every decision
      // stays held back rather than judged against stand-in numbers.
      .catch((e) => {
        if (alive) setMetaError((e as Error).message);
      });
    listRejectionReasons(token, 'M11')
      .then((rs) => {
        if (!alive) return;
        setReasons(rs);
        setReasonCode((prev) => prev || rs[0]?.reasonCode || '');
      })
      .catch((e) => {
        if (alive) setError((e as Error).message);
      });
    return () => {
      alive = false;
    };
  }, [token]);

  if (error && !data) {
    return (
      <main aria-label="company-approval">
        <p role="alert">{error}</p>
      </main>
    );
  }
  if (!data) {
    return (
      <main aria-label="company-approval">
        <p>{t('closure.approval.loading')}</p>
      </main>
    );
  }

  const v = data.verification;
  // The tenant's thresholds, or nothing: a decision is never judged against
  // a stand-in number, so until they are read every decision is held back.
  const thr = meta?.thresholds ?? null;
  const shown = (n: number | undefined, fmt: (x: number) => string = String) => (n === undefined ? '—' : fmt(n));
  const preconditions = [
    {
      key: 'quality',
      label: t('closure.approval.pre.quality', { score: String(v.overallQualityScore ?? '—'), min: shown(thr?.qualityMin) }),
      ok: thr !== null && (v.overallQualityScore ?? 0) >= thr.qualityMin,
    },
    {
      key: 'brand',
      label: t('closure.approval.pre.brand', { score: String(v.brandComplianceScore ?? '—'), min: shown(thr?.brandMin) }),
      ok: thr !== null && (v.brandComplianceScore ?? 0) >= thr.brandMin,
    },
    {
      key: 'gps',
      label: t('closure.approval.pre.gps'),
      ok: v.gpsValidated === true,
    },
    {
      key: 'area',
      label: t('closure.approval.pre.area', { max: shown(thr?.areaMaxPct, decimal) }),
      ok: thr !== null && v.areaVariancePct !== null && v.areaVariancePct <= thr.areaMaxPct,
    },
    {
      key: 'status',
      label: t('closure.approval.pre.status'),
      ok: v.status === 'IN_DECISION',
    },
  ] as const;

  const allOk = preconditions.every((p) => p.ok);
  const remarksOk = thr !== null && remarks.trim().length >= thr.remarkMin;

  const decide = async (which: Decision) => {
    setBusy(true);
    setInfo(null);
    setError(null);
    try {
      const base = `/v1/closure/approval/${encodeURIComponent(verificationId)}`;
      if (which === 'APPROVE') {
        await apiRequest(`${base}/approve`, {
          method: 'POST',
          token,
          body: { approverRemarks: remarks, brandAttrs: {} },
        });
        setInfo('Approved');
      } else if (which === 'REJECT') {
        await apiRequest(`${base}/reject`, {
          method: 'POST',
          token,
          body: { rejectionReasonCode: reasonCode, approverRemarks: remarks },
        });
        setInfo('Rejected');
      } else if (which === 'REWORK') {
        await apiRequest(`${base}/rework`, {
          method: 'POST',
          token,
          body: { reworkTargetCode: reworkTarget, approverRemarks: remarks },
        });
        setInfo('Rework requested');
      } else {
        await apiRequest(`${base}/evidence`, {
          method: 'POST',
          token,
          body: { evidenceKindCode: evidenceKind, approverRemarks: remarks },
        });
        setInfo('Evidence requested');
      }
    } catch (e) {
      const m = e instanceof ApiError
        ? (typeof e.body === 'object' && e.body !== null && 'userMessage' in e.body
            ? String((e.body as { userMessage: unknown }).userMessage)
            : e.message)
        : (e as Error).message;
      setError(m);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="company-approval">
      <h1>
        {t('closure.approval.title')} <span aria-label="job-id">{data.jobNumber ?? '—'}</span>
      </h1>

      {metaError && <p role="alert" data-testid="closure-meta-error">{metaError}</p>}
      {error && <p role="alert">{error}</p>}
      {info && <p role="status">{info}</p>}

      <section aria-label="preconditions">
        <h2>{t('closure.approval.preH')}</h2>
        <ul>
          {preconditions.map((p) => (
            <li key={p.key} aria-label={`pre-${p.key}`}>
              {p.ok ? '✔︎' : '✖'} {p.label}
            </li>
          ))}
        </ul>
      </section>

      <section aria-label="remarks-section">
        <label htmlFor="approver-remarks">{t('closure.approval.remarksLabel')}</label>
        <textarea
          id="approver-remarks"
          aria-label="remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
        />
        <p aria-label="remarks-counter">
          {remarks.trim().length}/{shown(thr?.remarkMin)}
        </p>
      </section>

      <section aria-label="actions">
        <button
          type="button"
          aria-label="approve"
          disabled={!allOk || !remarksOk || busy}
          title={
            !allOk
              ? t('closure.approval.tip.pre')
              : !remarksOk
                ? t('closure.approval.tip.remarks')
                : ''
          }
          onClick={() => void decide('APPROVE')}
        >
          {t('closure.approval.approve')}
        </button>

        <label htmlFor="reason-code">{t('closure.approval.rejectReason')}</label>
        <select
          id="reason-code"
          aria-label="reason"
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
        >
          {reasons.map((r) => (
            <option key={r.reasonId} value={r.reasonCode}>
              {r.reasonDescription || r.reasonCode}
            </option>
          ))}
        </select>
        <button
          type="button"
          aria-label="reject"
          disabled={!remarksOk || busy || !reasonCode}
          onClick={() => void decide('REJECT')}
        >
          {t('closure.approval.reject')}
        </button>

        <label htmlFor="rework-target">{t('closure.approval.reworkTarget')}</label>
        <select
          id="rework-target"
          aria-label="rework-target"
          value={reworkTarget}
          onChange={(e) => setReworkTarget(e.target.value)}
        >
          {(meta?.reworkTargets ?? []).map((code) => (
            <option key={code} value={code}>
              {humanize(code)}
            </option>
          ))}
        </select>
        <button
          type="button"
          aria-label="rework"
          disabled={!remarksOk || busy || !reworkTarget}
          onClick={() => void decide('REWORK')}
        >
          {t('closure.approval.requestRework')}
        </button>

        <label htmlFor="evidence-kind">{t('closure.approval.evidenceKind')}</label>
        <select
          id="evidence-kind"
          aria-label="evidence-kind"
          value={evidenceKind}
          onChange={(e) => setEvidenceKind(e.target.value)}
        >
          {(meta?.evidenceKinds ?? []).map((code) => (
            <option key={code} value={code}>
              {humanize(code)}
            </option>
          ))}
        </select>
        <button
          type="button"
          aria-label="evidence"
          disabled={!remarksOk || busy || !evidenceKind}
          onClick={() => void decide('EVIDENCE')}
        >
          {t('closure.approval.requestEvidence')}
        </button>
      </section>

      <Link to={`/closure/signoff/${v.verificationId}`} aria-label="go-signoff">
        {t('closure.approval.goSignoff')}
      </Link>
    </main>
  );
}
